import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reply, Copy, Trash2, Smile, Pencil, Star } from 'lucide-react';
import { hapticTap } from '@/lib/native-haptics';

interface MessageContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onReply: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onDeleteForMe?: () => void;
  onEdit?: () => void;
  onReact: () => void;
  onStar?: () => void;
  isOwn: boolean;
  isStarred?: boolean;
  messageContent: string;
}

export function MessageContextMenu({
  isOpen,
  onClose,
  onReply,
  onCopy,
  onDelete,
  onDeleteForMe,
  onEdit,
  onReact,
  onStar,
  isOwn,
  isStarred,
  messageContent,
}: MessageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  const [flipUp, setFlipUp] = useState(false);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      // Default is below; flip above only if overflows bottom
      if (rect.bottom > window.innerHeight - 8) {
        setFlipUp(true);
      }
    } else {
      setFlipUp(false);
    }
  }, [isOpen]);

  const actions = [
    { icon: Reply, label: 'Reply', action: onReply },
    { icon: Smile, label: 'React', action: onReact },
    ...(onStar ? [{ icon: Star, label: isStarred ? 'Unstar' : 'Star', action: onStar, starred: isStarred }] : []),
    { icon: Copy, label: 'Copy', action: () => { navigator.clipboard.writeText(messageContent); onCopy(); } },
    ...(isOwn && onEdit ? [{ icon: Pencil, label: 'Edit', action: onEdit }] : []),
    ...(onDeleteForMe ? [{ icon: Trash2, label: 'Delete for Me', action: onDeleteForMe, destructive: true }] : []),
    ...(isOwn ? [{ icon: Trash2, label: 'Delete for Everyone', action: onDelete, destructive: true }] : []),
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.88, y: flipUp ? 6 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: flipUp ? 6 : -6 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
            className="absolute z-50 bg-card border border-border/50 rounded-2xl shadow-elevated p-1 min-w-[150px] backdrop-blur-xl"
            style={{
              ...(flipUp
                ? { bottom: '100%', marginBottom: 6 }
                : { top: '100%', marginTop: 6 }),
              ...(isOwn ? { right: 0 } : { left: 0 }),
            }}
          >
            {actions.map(({ icon: Icon, label, action, destructive }) => (
              <button
                key={label}
                onClick={() => {
                  hapticTap();
                  action();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-[13px] font-medium rounded-xl hover:bg-muted/60 active:bg-muted transition-colors"
              >
                <Icon className={`h-4 w-4 ${destructive ? 'text-destructive' : 'text-muted-foreground'}`} />
                <span className={destructive ? 'text-destructive' : ''}>{label}</span>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
